import { createFileRoute, Link } from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { useNavbar } from '@/components/navbar'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, FileText, Slash, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PhoneNumberInput } from '@/components/ui/phone-number-input'
import type { Country } from 'react-phone-number-input'
import { errorToast } from '@/lib/utils/ToastUtils'
import { toast } from 'sonner'
import { cn } from '@/lib/utils/ui'
import {
  getSourceRefPlaceholder,
  LeadSourceChannelField,
} from '@/components/leads/LeadSourceChannelField'
import { LeadFormResponsesSheet } from '@/components/leads/LeadFormResponsesSheet'
import LeadPaymentLinkDialog from '@/components/leads/LeadPaymentLinkDialog'
import { LeadNotesActivityCard } from '@/components/leads/LeadNotesActivityCard'
import NewClientDialog from '@/components/client/newClientDialog'
import { useOrg } from '@/lib/states/session/hooks'
import { LeadTagsField } from '@/components/leads/LeadTagsField'
import { Skeleton } from '@/components/ui/skeleton'
import { useDocumentTitle } from '@/lib/hooks/useDocumentTitle'
import { trpc } from '@/lib/trpc/client'
import { trpcVanilla } from '@/lib/trpc/vanilla'
import { patchListCache, patchQueryCache } from '@trainrr/api-client'

export const Route = createFileRoute('/_auth/_dashboard/leads/$leadId')({
  beforeLoad: () => {
    requirePermission('organisation.manage')
  },
  pendingComponent: LeadDetailSkeleton,
  component: LeadDetailPage,
})

type LeadDetail = Awaited<ReturnType<typeof trpcVanilla.leads.get.query>>

const emptyLeadFormValues: LeadFormValues = {
  name: '',
  email: '',
  phone: '',
  sourceChannel: '',
  sourceRef: '',
}

const leadSchema = z.object({
  name: z.string().optional().or(z.literal('')),
  email: z.string().email('Enter a valid email').optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  sourceChannel: z.string().optional().or(z.literal('')),
  sourceRef: z.string().optional().or(z.literal('')),
})

type LeadFormValues = z.infer<typeof leadSchema>

function getLeadFormValues(
  lead: Pick<
    LeadDetail,
    'name' | 'email' | 'phone' | 'source_channel' | 'source_ref'
  >,
): LeadFormValues {
  return {
    name: lead.name ?? '',
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    sourceChannel: lead.source_channel ?? '',
    sourceRef: lead.source_ref ?? '',
  }
}

function LeadDetailPage() {
  const { leadId } = Route.useParams()
  const { setContent } = useNavbar()
  const org = useOrg()
  const utils = trpc.useUtils()
  const form = useForm<LeadFormValues>({
    resolver: zodResolver(leadSchema),
    defaultValues: emptyLeadFormValues,
  })
  const [saving, setSaving] = useState(false)
  const [stageSaving, setStageSaving] = useState(false)
  const changeStage = trpc.leads.changeStage.useMutation()
  const updateLead = trpc.leads.update.useMutation()
  const [stageId, setStageId] = useState('')
  const [responsesOpen, setResponsesOpen] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [defaultCountry, setDefaultCountry] = useState<Country>('US')
  const isDirty = form.formState.isDirty
  const watchedName = form.watch('name')
  const watchedEmail = form.watch('email')
  const watchedPhone = form.watch('phone')
  const sourceChannel = form.watch('sourceChannel')
  const hasSourceChannel = !!sourceChannel?.trim()

  const {
    data: leadDetail,
    error: leadError,
    isLoading: leadLoading,
  } = trpc.leads.get.useQuery(
    { leadId },
    { enabled: Boolean(org?.id) && Boolean(leadId) },
  )
  const {
    data: packageRows,
    error: packagesError,
    isLoading: packagesLoading,
  } = trpc.packages.listForCheckout.useQuery(undefined, {
    enabled: Boolean(org?.id),
  })

  const stageOptions = leadDetail?.stages ?? []
  const stageHistoryRows = leadDetail?.stageHistory ?? []
  const hasResponses = leadDetail?.hasResponses ?? false
  const packages = packageRows ?? []
  const queryError = leadError ?? packagesError
  const isLoading = !org || leadLoading || packagesLoading
  const activeLead = leadDetail
  const orgRef = org?.slug ?? org?.id ?? ''
  const leadTitle = watchedName?.trim() || activeLead?.name || activeLead?.email

  useDocumentTitle(leadTitle || 'Lead')

  const breadcrumb = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to="/leads">Leads</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <Slash />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>
              {watchedName?.trim() || activeLead?.name || 'Lead'}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    [activeLead?.name, watchedName],
  )

  useEffect(() => {
    setContent(breadcrumb)
    const resetId = window.setTimeout(() => setContent(breadcrumb), 0)
    return () => {
      window.clearTimeout(resetId)
      setContent(null)
    }
  }, [breadcrumb, setContent])

  useEffect(() => {
    const inferred = inferCountryFromLocale()
    if (inferred) {
      setDefaultCountry(inferred)
    }
  }, [])

  useEffect(() => {
    if (!leadDetail) return
    setStageId(leadDetail.stage_id ?? '')
  }, [leadDetail?.id, leadDetail?.stage_id])

  useEffect(() => {
    if (!leadDetail) return
    form.reset(getLeadFormValues(leadDetail))
  }, [
    form,
    leadDetail?.id,
    leadDetail?.name,
    leadDetail?.email,
    leadDetail?.phone,
    leadDetail?.source_channel,
    leadDetail?.source_ref,
  ])

  useEffect(() => {
    if (!hasSourceChannel && form.getValues('sourceRef')) {
      form.setValue('sourceRef', '')
      form.clearErrors('sourceRef')
    }
  }, [form, hasSourceChannel])

  const sourceRefPlaceholder = useMemo(() => {
    return getSourceRefPlaceholder(sourceChannel)
  }, [sourceChannel])

  const convertPrefill = useMemo(() => {
    const fullName = watchedName?.trim() || activeLead?.name || ''
    const nameParts = fullName.split(/\s+/).filter(Boolean)
    const firstName = nameParts[0] ?? ''
    const lastName = nameParts.slice(1).join(' ')
    const email = watchedEmail?.trim() || activeLead?.email || ''
    const phone = watchedPhone?.trim() || activeLead?.phone || ''
    return { firstName, lastName, email, phone }
  }, [
    activeLead?.email,
    activeLead?.name,
    activeLead?.phone,
    watchedEmail,
    watchedName,
    watchedPhone,
  ])

  const stageDurations = useMemo(() => {
    const now = new Date()
    const totals = new Map<string, number>()

    for (const entry of stageHistoryRows) {
      const start = new Date(entry.entered_at)
      const end = entry.exited_at ? new Date(entry.exited_at) : now
      const diff = Math.max(0, end.getTime() - start.getTime())
      totals.set(entry.stage_id, (totals.get(entry.stage_id) ?? 0) + diff)
    }

    return totals
  }, [stageHistoryRows])

  if (queryError) {
    throw queryError
  }

  if (isLoading) {
    return <LeadDetailSkeleton />
  }

  if (!activeLead) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">
        Lead not found
      </div>
    )
  }

  const handleSubmit = async (values: LeadFormValues) => {
    if (!activeLead) return
    setSaving(true)
    const name = values.name?.trim() || ''
    const sourceChannelValue = values.sourceChannel?.trim() || null
    try {
      const updatedLead = await updateLead.mutateAsync({
        leadId: activeLead.id,
        ...(name ? { name } : {}),
        email: values.email?.trim() || null,
        phone: values.phone?.trim() || null,
        sourceChannel: sourceChannelValue,
        sourceRef:
          sourceChannelValue && values.sourceRef?.trim()
            ? values.sourceRef.trim()
            : null,
      })

      patchQueryCache(utils.leads.get, { leadId }, (old) => ({
        ...old,
        ...updatedLead,
      }))
      patchListCache(utils.leads.list, (old) =>
        old.map((lead) =>
          lead.id === updatedLead.id
            ? {
                ...lead,
                name: updatedLead.name,
                email: updatedLead.email,
                phone: updatedLead.phone,
                sourceChannel: updatedLead.source_channel,
                sourceRef: updatedLead.source_ref,
              }
            : lead,
        ),
      )
      form.reset(getLeadFormValues(updatedLead))
      toast.success('Lead updated')
    } catch (error) {
      errorToast(error instanceof Error ? error : null)
    } finally {
      setSaving(false)
    }
  }

  const handleStageChange = async (nextId: string) => {
    if (!activeLead || !nextId || nextId === stageId) return
    const previousStageId = stageId
    const nextStage = stageOptions.find((stage) => stage.id === nextId)
    setStageId(nextId)
    setStageSaving(true)
    try {
      await changeStage.mutateAsync({
        leadId: activeLead.id,
        stageId: nextId,
      })
    } catch (error) {
      setStageId(previousStageId)
      errorToast(error instanceof Error ? error : null)
      setStageSaving(false)
      return
    }
    setStageSaving(false)

    patchQueryCache(utils.leads.get, { leadId }, (old) => ({
      ...old,
      stage_id: nextId,
    }))
    patchListCache(utils.leads.list, (old) =>
      old.map((lead) =>
        lead.id === activeLead.id
          ? {
              ...lead,
              stageId: nextId,
              stageName: nextStage?.name ?? lead.stageName,
              stageKey: nextStage?.key ?? lead.stageKey,
            }
          : lead,
      ),
    )
    void utils.leads.get.invalidate({ leadId })
    toast.success('Stage updated')
  }

  const formatDurationLabel = (ms?: number) => {
    const days = Math.floor((ms ?? 0) / (1000 * 60 * 60 * 24))
    return `${days} day${days === 1 ? '' : 's'}`
  }

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex">
        <div className="flex-1 space-y-6 p-8 pt-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  aria-label="Back to leads"
                >
                  <Link to="/leads">
                    <ArrowLeft className="h-4 w-4" />
                  </Link>
                </Button>
                <span>
                  {watchedName?.trim() || activeLead.name || 'Anonymous lead'}
                </span>
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasResponses ? (
                <Button
                  type="button"
                  variant="outline"
                  aria-label="View form response"
                  onClick={() => setResponsesOpen(true)}
                >
                  <FileText />
                  <span className="hidden sm:inline">View form response</span>
                </Button>
              ) : null}
              <LeadPaymentLinkDialog
                leadId={activeLead.id}
                leadName={
                  watchedName?.trim() || activeLead.name || 'Anonymous lead'
                }
                packages={packages}
                orgRef={orgRef}
              />
              <Button
                type="button"
                variant="outline"
                aria-label="Convert to client"
                onClick={() => setConvertOpen(true)}
              >
                <UserPlus />
                <span className="hidden sm:inline">Convert to client</span>
              </Button>
              <Button
                type="submit"
                form="lead-edit-form"
                disabled={saving || !isDirty}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>

          {stageOptions.length > 0 ? (
            <div className="w-full">
              <div className="flex flex-wrap items-center overflow-hidden rounded-full border border-border bg-muted/30">
                {stageOptions.map((stage, index) => {
                  const activeIndex = stageOptions.findIndex(
                    (item) => item.id === stageId,
                  )
                  const isCompleted =
                    activeIndex >= 0 ? index <= activeIndex : false
                  const isClosed = stage.is_closed
                  const durationLabel = formatDurationLabel(
                    stageDurations.get(stage.id),
                  )
                  return (
                    <TooltipProvider key={stage.id} delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => handleStageChange(stage.id)}
                            disabled={stageSaving}
                            className={cn(
                              'relative flex-1 px-4 py-2 text-sm font-medium transition',
                              'min-w-0 whitespace-nowrap',
                              'min-w-[120px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                              isCompleted
                                ? 'bg-emerald-600 text-white [.dark_&]:bg-emerald-700 [.dark_&]:text-emerald-50'
                                : isClosed
                                  ? 'bg-muted text-muted-foreground [.dark_&]:bg-zinc-800 [.dark_&]:text-zinc-400'
                                  : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 [.dark_&]:bg-emerald-950 [.dark_&]:text-emerald-300 [.dark_&]:hover:bg-emerald-900',
                            )}
                          >
                            <span className="block truncate">
                              {stage.name ?? 'Untitled'}
                            </span>
                            {index < stageOptions.length - 1 ? (
                              <span
                                className={cn(
                                  'absolute right-0 top-0 h-full w-4 translate-x-1/2 bg-inherit',
                                )}
                                style={{
                                  clipPath:
                                    'polygon(0 0, 100% 50%, 0 100%, 0 0)',
                                }}
                              />
                            ) : null}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <span className="text-sm">
                            {stage.name ?? 'Untitled'}: {durationLabel}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )
                })}
              </div>
            </div>
          ) : null}

          <Card>
            <CardHeader className="text-sm font-medium text-muted-foreground">
              Lead details
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  id="lead-edit-form"
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-4"
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Jane Doe" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input placeholder="jane@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <PhoneNumberInput
                              defaultCountry={defaultCountry}
                              value={field.value || ''}
                              onChange={(value) => field.onChange(value)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="sourceChannel"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Source channel</FormLabel>
                          <FormControl>
                            <LeadSourceChannelField
                              value={field.value}
                              onChange={(value) => {
                                form.setValue('sourceChannel', value, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                })
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className={cn(!hasSourceChannel && 'hidden')}>
                      <FormField
                        control={form.control}
                        name="sourceRef"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Source details</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={sourceRefPlaceholder}
                                disabled={!hasSourceChannel}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <LeadTagsField orgId={activeLead.org_id} leadId={activeLead.id} />
                </form>
              </Form>
            </CardContent>
          </Card>
          <LeadNotesActivityCard
            leadId={activeLead.id}
            stageHistory={stageHistoryRows}
            leadCreatedAt={activeLead.created_at}
          />
        </div>
      </div>
      <NewClientDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        prefill={convertPrefill}
      />
      <LeadFormResponsesSheet
        leadId={activeLead.id}
        open={responsesOpen}
        onOpenChange={setResponsesOpen}
      />
    </div>
  )
}

function LeadDetailSkeleton() {
  return (
    <div className="w-full">
      <div className="flex flex-col md:flex">
        <div className="flex-1 space-y-6 p-8 pt-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-10 rounded-md" />
              <Skeleton className="h-9 w-64" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Skeleton className="h-10 w-40" />
              <Skeleton className="h-10 w-36" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>

          <div className="flex flex-wrap items-center overflow-hidden rounded-full border border-border bg-muted/30">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={index}
                className="h-10 min-w-[120px] flex-1 rounded-none"
              />
            ))}
          </div>

          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-24" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="space-y-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <div className="flex flex-wrap gap-2">
                  <Skeleton className="h-8 w-20 rounded-full" />
                  <Skeleton className="h-8 w-24 rounded-full" />
                  <Skeleton className="h-8 w-16 rounded-full" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

function inferCountryFromLocale(): Country | null {
  if (typeof navigator === 'undefined') return null
  const locale = navigator.language || ''
  const match = locale.match(/-([A-Z]{2})$/i)
  if (!match) return null
  return match[1].toUpperCase() as Country
}
